<template>
  <div style="display: grid; grid-template-columns: auto; row-gap: 1em; width: 19.25em;">
    <div style="display: flex; justify-content: center; align-items: center;">
      <div class="datepicker-button" @click="onLastMonth">先月</div>
      <div style="width: 66.666667%; font-size: 1.125em; text-align: center;">
        {{ displayYear }}年{{ displayMonth }}月
      </div>
      <div class="datepicker-button" @click="onNextMonth">来月</div>
    </div>
    <div style="display: grid; grid-template-columns: repeat(7, 1fr); grid-auto-rows: 2.75em;">
      <div
        v-for="day in displayDayOfWeek"
        :key="day.en"
        class="day-of-week"
        :class="{ 'day-of-week--sunday': day.sunday, 'day-of-week--saturday': day.saturday }"
      >
        {{ day.ja }}
      </div>
      <template v-for="date in displayDates">
        <template v-if="date.thisMonth">
          <div
            v-if="!date.disabled"
            :key="date.date"
            class="date"
            :class="{ 'date--sunday': date.sunday, 'date--saturday': date.saturday, 'date--selected': date.selected }"
            @click="onClick($event, { dateOfMonth: date.dateOfMonth, month: date.month, year: date.year })"
          >
            <div class="date__text" :class="{ 'date__text--range': date.range, 'date__text--selected': date.selected }">{{ date.dateOfMonth }}</div>
          </div>
          <div
            v-else
            :key="date.date"
            class="date date--disabled"
            :class="{ 'date--sunday': date.sunday, 'date--saturday': date.saturday, 'date--selected': date.selected }"
          >
            <div class="date__text" :class="{ 'date__text--selected': date.selected }">{{ date.dateOfMonth }}</div>
          </div>
        </template>
        <template v-else>
          <div :key="date.date"></div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  model: {
    event: 'change'
  },
  props: {
    value: {
      type: [String, Object],
      required: false,
      default: null
    },
    range: {
      type: Boolean,
      required: false,
      default: false
    },
    isEnd: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    month: null
  }),
  computed: {
    selectedValue() {
      const { value } = this

      if (this.range) {
        if (value) {
          return this.isEnd ? value.end : value.start
        } else {
          return null
        }
      } else {
        return value
      }
    },
    unselectedValue() {
      const { value } = this

      if (this.range) {
        if (value) {
          return this.isEnd ? value.start : value.end
        } else {
          return null
        }
      } else {
        return value
      }
    },
    beginDate() {
      const momentObject = moment(this.month)
      const dayOfWeek = momentObject.day()
      return momentObject.subtract(dayOfWeek, 'days').format('YYYY-MM-DD')
    },
    endDate() {
      const momentObject = moment(this.month)
      const daysInMonth = momentObject.daysInMonth()
      const lastDate = momentObject.clone().date(daysInMonth).format('YYYY-MM-DD')
      return moment(lastDate).add(7 - moment(lastDate).day() - 1, 'days').format('YYYY-MM-DD')
    },
    displayYear() {
      return moment(this.month).year()
    },
    displayMonth() {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12][moment(this.month).month()]
    },
    displayDayOfWeek() {
      return [
        { en: 'sunday', ja: '日', sunday: true, saturday: false },
        { en: 'monday', ja: '月', sunday: false, saturday: false },
        { en: 'tuesday', ja: '火', sunday: false, saturday: false },
        { en: 'wednesday', ja: '水', sunday: false, saturday: false },
        { en: 'thursday', ja: '木', sunday: false, saturday: false },
        { en: 'friday', ja: '金', sunday: false, saturday: false },
        { en: 'saturday', ja: '土', sunday: false, saturday: true }
      ]
    },
    displayDates() {
      const { value, beginDate } = this
      const dates = []
      const n = moment(this.endDate).diff(beginDate, 'days') + 1

      for (let i = 0; i < n; i++) {
        const date = moment(beginDate).add(i, 'days').format('YYYY-MM-DD')
        const dateObject = moment(date)

        const { date: dateOfMonth, months: month, years: year } = dateObject.toObject()
        const dayOfWeek = dateObject.day()

        const thisMonth = dateObject.isSame(this.month, 'month')
        const sunday = (dayOfWeek === 0)
        const saturday = (dayOfWeek === 6)
        const selected = dateObject.isSame(this.selectedValue, 'day')

        let disabled = false

        if (this.range && value) {
          if (this.isEnd && value.start && moment(date).isBefore(value.start)) {
            disabled = true
          }

          if (!this.isEnd && value.end && moment(date).isAfter(value.end)) {
            disabled = true
          }
        }

        let range = false

        if (this.range && value && value.start && value.end) {
          range = moment(date).isBetween(value.start, value.end, 'day', '[]')
        }

        dates.push({ date, dateOfMonth, month, year, thisMonth, sunday, saturday, selected, disabled, range })
      }

      return dates
    }
  },
  watch: {
    isEnd() {
      this.loadMonth()
    }
  },
  created() {
    this.loadMonth()
  },
  methods: {
    onLastMonth() {
      const lastMonth = moment(this.month).subtract(1, 'month')
      this.month = lastMonth
    },
    onNextMonth() {
      const nextMonth = moment(this.month).add(1, 'month')
      this.month = nextMonth
    },
    onClick(event, payload) {
      const { dateOfMonth, month, year } = payload
      let value = null

      if (this.selectedValue) {
        value = moment(this.selectedValue).year(year).month(month).date(dateOfMonth).format()
      } else {
        const time = { hour: 0, minute: 0, second: 0 }

        if (this.isEnd) {
          time.hour = 23
          time.minute = 59
          time.second = 59
        }

        value = moment().year(year).month(month).date(dateOfMonth).hour(time.hour).minute(time.minute).second(time.second).millisecond(0).format()
      }

      const { value: dataValue } = this

      if (this.range) {
        const object = dataValue ? { start: dataValue.start, end: dataValue.end } : { start: null, end: null }
        const key = this.isEnd ? 'end' : 'start'
        object[key] = value
        this.$emit('change', object)
      } else {
        this.$emit('change', value)
      }
    },
    loadMonth() {
      const { selectedValue, unselectedValue } = this
      let momentObject = null

      if (selectedValue) {
        momentObject = moment(selectedValue)
      } else if (unselectedValue) {
        momentObject = moment(unselectedValue)
      } else {
        momentObject = moment()
      }

      this.month = momentObject.format('YYYY-MM')
    }
  }
}
</script>

<style scoped>
.day-of-week {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.75em;
}

.day-of-week--sunday {
  color: #f56565;
}

.day-of-week--saturday {
  color: #4299e1;
}

.date {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.75em;
  border-radius: 0.125em;
  cursor: pointer;
}

.date:hover {
  background-color: #ebf8ff;
  color: #2b6cb0;
}

.date--disabled {
  opacity: 0.25;
  cursor: default;
}

.date--disabled:hover {
  background-color: inherit;
  color: inherit;
}

.date--sunday {
  color: #f56565;
}

.date--saturday {
  color: #4299e1;
}

.date--selected {
  position: relative;
  font-weight: bold;
}

.date__text {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2.5em;
  height: 2.5em;
  border-radius: 50%;
}

.date__text--range {
  background-color: #b2f5ea;
}

.date__text--selected {
  background-color: #1f65b5;
  color: #e6fffa;
}

.datepicker-button {
  font-size: 0.75rem;
  color: #134b8b;
  cursor: pointer;
}

.datepicker-button:hover {
  color: #134b8b;
}
</style>
